import { addDays, dateOnly, type DateOnly } from './date-only'

export interface Holiday {
  date: DateOnly
  name: string
  scope: 'national' | 'regional' | 'municipal' | 'custom'
}

export interface HolidayProvider {
  readonly id: string
  /** Where the definition comes from (law, regulation, organization config). */
  readonly source: string
  holidaysForYear(year: number): Holiday[]
}

/** Gregorian Easter Sunday (Meeus/Jones/Butcher algorithm). */
export function easterSunday(year: number): DateOnly {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return dateOnly(year, month, day)
}

/**
 * Portuguese mandatory national public holidays (feriados obrigatórios).
 * Source: Código do Trabalho, artigo 234.º (Lei n.º 7/2009, na redação da
 * Lei n.º 8/2016, que repôs Corpo de Deus, 5 de Outubro, 1 de Novembro e
 * 1 de Dezembro). Carnival and municipal holidays are optional and must be
 * configured per organization (CustomHolidayProvider).
 */
export class PortugueseNationalHolidays implements HolidayProvider {
  readonly id = 'pt-national'
  readonly source = 'Código do Trabalho, art. 234.º (Lei n.º 8/2016)'

  holidaysForYear(year: number): Holiday[] {
    const easter = easterSunday(year)
    const fixed: Array<[number, number, string]> = [
      [1, 1, 'Ano Novo'],
      [4, 25, 'Dia da Liberdade'],
      [5, 1, 'Dia do Trabalhador'],
      [6, 10, 'Dia de Portugal'],
      [8, 15, 'Assunção de Nossa Senhora'],
      [10, 5, 'Implantação da República'],
      [11, 1, 'Dia de Todos os Santos'],
      [12, 1, 'Restauração da Independência'],
      [12, 8, 'Imaculada Conceição'],
      [12, 25, 'Natal'],
    ]
    const list: Holiday[] = fixed.map(([m, d, name]) => ({ date: dateOnly(year, m, d), name, scope: 'national' }))
    list.push({ date: addDays(easter, -2), name: 'Sexta-feira Santa', scope: 'national' })
    list.push({ date: easter, name: 'Domingo de Páscoa', scope: 'national' })
    list.push({ date: addDays(easter, 60), name: 'Corpo de Deus', scope: 'national' })
    return list.sort((x, y) => (x.date < y.date ? -1 : 1))
  }
}

/** Organization-defined holidays (municipal holiday, Carnival, office closures). */
export class CustomHolidayProvider implements HolidayProvider {
  readonly id = 'custom'
  readonly source = 'Configuração do gabinete'
  constructor(private readonly holidays: Holiday[]) {}

  holidaysForYear(year: number): Holiday[] {
    return this.holidays.filter((h) => h.date.startsWith(`${year}-`))
  }
}
